import { useId, useState } from "react";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { CalendarDays, ChevronDown, Flag, UserOutline } from "@acto/web-common/components";
import type { Todo } from "@acto/taskpane/core/domain";
import { Card } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { daysOverdue, startOfToday, todoDueDateLabel } from "@acto/taskpane/field-sales-2/utils/todoDueLabels";

export interface TodoDelegatedCardProps {
  todos: Todo[];
  defaultOpen?: boolean;
}

// The Card gap would leave a blank band under the header while the Collapse is closed.
const AccordionCard = styled(Card)({ gap: 0 });

const ToggleRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["17"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["130"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Chevron = styled(ChevronDown)<{ open: boolean }>(({ theme }) => ({
  width: 16,
  height: 16,
  color: theme.fieldSalesTwo.colors.neutral[800],
  transition: "transform 160ms ease",
  transform: "rotate(-90deg)",
  variants: [{ props: { open: true }, style: { transform: "rotate(0deg)" } }],
}));

// Padding, not margin: Collapse measures a plain wrapper div, and a top margin would collapse
// through it — the measured height then comes up short and the clip cuts the last row.
const HeaderSeparator = styled("div")(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[24],
  borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
}));

const Rows = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  paddingTop: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[16],
  "&:not(:last-child)": { borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[300]}` },
}));

const Title = styled("span")<{ done: boolean }>(({ theme }) => ({
  minWidth: 0,
  overflowWrap: "anywhere",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["16"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  variants: [
    {
      props: { done: true },
      style: { color: theme.fieldSalesTwo.colors.neutral[800], textDecoration: "line-through" },
    },
  ],
}));

const Meta = styled("span")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["440"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": { width: 18, height: 18 },
}));

const MetaPiece = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const MetaSeparator = styled("span")(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

/**
 * Open to-dos assigned to someone else, collapsed behind an accordion header. Read-only —
 * a to-do assigned to another person is never completable by the viewer.
 */
export function TodoDelegatedCard({ todos, defaultOpen = false }: TodoDelegatedCardProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.todoChecklist");
  const tTodos = useTranslations("fieldSalesTwo.todosList");
  const { locale } = useLocale();
  // Null until the viewer toggles, so a defaultOpen settled by a later render still applies: the
  // todos can resolve before the current user, and until then every row counts as delegated.
  const [toggledOpen, setToggledOpen] = useState<boolean | null>(null);
  const open = toggledOpen ?? defaultOpen;
  const id = useId();
  const listId = `${id}-delegated`;
  const titleId = `${id}-delegated-title`;

  if (todos.length === 0) return null;

  const today = startOfToday();

  return (
    <AccordionCard>
      <ToggleRow
        type="button"
        id={titleId}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setToggledOpen(!open)}
      >
        {t("delegatedTitle")}
        <Chevron open={open} aria-hidden="true" />
      </ToggleRow>
      <Collapse open={open} id={listId} role="list" aria-labelledby={titleId}>
        <HeaderSeparator />
        <Rows>
          {todos.map((todo) => {
            const isCompleted = todo.isCompleted();
            const overdueDays = todo.dueDate && !isCompleted ? daysOverdue(todo.dueDate, today) : 0;
            const isOverdue = overdueDays > 0;
            const dateLabel = isCompleted
              ? undefined
              : isOverdue
                ? tTodos("meta.overdueDays", { count: overdueDays })
                : todoDueDateLabel(todo.dueDate, locale);
            return (
              <Row key={todo.id} role="listitem">
                <Title done={isCompleted}>{todo.title.length === 0 ? (todo.description ?? "") : todo.title}</Title>
                {(todo.assignee?.displayName || dateLabel) && (
                  <Meta>
                    {todo.assignee?.displayName && (
                      <MetaPiece>
                        <UserOutline aria-hidden="true" />
                        {todo.assignee.displayName}
                      </MetaPiece>
                    )}
                    {todo.assignee?.displayName && dateLabel && <MetaSeparator aria-hidden="true">·</MetaSeparator>}
                    {dateLabel && (
                      <MetaPiece>
                        {isOverdue ? <Flag aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
                        {dateLabel}
                      </MetaPiece>
                    )}
                  </Meta>
                )}
              </Row>
            );
          })}
        </Rows>
      </Collapse>
    </AccordionCard>
  );
}
